import { useMemo } from "react";
import { createStore, useStore } from "zustand";
import { userMediaStore } from "./media-monitor";

export type DeviceFilterKind = "microphone" | "video" | "speaker";
export type DeviceList = MediaDeviceInfo[];

type DeviceFilterState = {
  mode: "all" | "custom";
  visibleIds: string[];
};

type DeviceFiltersStoreState = {
  filters: Record<DeviceFilterKind, DeviceFilterState>;
  setAll: (type: DeviceFilterKind, deviceIds: string[]) => void;
  setNone: (type: DeviceFilterKind) => void;
  toggleDevice: (
    type: DeviceFilterKind,
    deviceId: string,
    deviceIds: string[],
  ) => void;
};

const deviceFiltersStore = createStore<DeviceFiltersStoreState>((set) => ({
  filters: {
    microphone: { mode: "all", visibleIds: [] },
    video: { mode: "all", visibleIds: [] },
    speaker: { mode: "all", visibleIds: [] },
  },
  setAll: (type, deviceIds) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [type]: { mode: "all", visibleIds: deviceIds },
      },
    })),
  setNone: (type) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [type]: { mode: "custom", visibleIds: [] },
      },
    })),
  toggleDevice: (type, deviceId, deviceIds) =>
    set((state) => {
      const filter = state.filters[type];
      const baseIds = filter.mode === "custom" ? filter.visibleIds : deviceIds;
      const nextIds = new Set(baseIds);
      if (nextIds.has(deviceId)) {
        nextIds.delete(deviceId);
      } else {
        nextIds.add(deviceId);
      }
      return {
        filters: {
          ...state.filters,
          [type]: { mode: "custom", visibleIds: Array.from(nextIds) },
        },
      };
    }),
}));

export const useDeviceFilters = () => {
  const filters = useStore(deviceFiltersStore, (state) => state.filters);
  const setAll = useStore(deviceFiltersStore, (state) => state.setAll);
  const setNone = useStore(deviceFiltersStore, (state) => state.setNone);
  const toggleDevice = useStore(deviceFiltersStore, (state) => state.toggleDevice);

  return useMemo(
    () => ({ filters, setAll, setNone, toggleDevice }),
    [filters, setAll, setNone, toggleDevice],
  );
};

type DeviceFilterConfig = {
  type: DeviceFilterKind;
  filterFn?: (devices: DeviceList) => DeviceList;
};

type UseDevicesOptions = {
  filters?: DeviceFilterConfig | DeviceFilterConfig[];
};

const filterSelectableDevices = (deviceList: DeviceList | null) => {
  if (!deviceList) return [];
  const concreteDevices = deviceList.filter(
    (device) =>
      device.deviceId !== "default" &&
      device.deviceId !== "communications" &&
      device.deviceId !== "",
  );
  return concreteDevices.length > 0 ? concreteDevices : deviceList;
};

const applyVisibilityFilter = (
  deviceList: DeviceList,
  filter: DeviceFilterState,
) => {
  if (filter.mode === "all") return deviceList;
  const visibleIds = new Set(filter.visibleIds);
  return deviceList.filter((device) => visibleIds.has(device.deviceId));
};

const normalizeFilters = (filters?: DeviceFilterConfig | DeviceFilterConfig[]) =>
  !filters ? [] : Array.isArray(filters) ? filters : [filters];

export const useDevices = (options?: UseDevicesOptions) => {
  const devicesState = useStore(userMediaStore, (s) => s.devices);
  const filters = useStore(deviceFiltersStore, (s) => s.filters);
  const filterConfigs = normalizeFilters(options?.filters);

  const rawDevices = useMemo(
    () => ({
      microphone: devicesState.audio.availableDevices ?? [],
      video: devicesState.video.availableDevices ?? [],
      speaker: devicesState.speaker.availableDevices ?? [],
    }),
    [devicesState],
  );

  const selectableDevices = useMemo(
    () => ({
      microphone: filterSelectableDevices(rawDevices.microphone),
      video: filterSelectableDevices(rawDevices.video),
      speaker: filterSelectableDevices(rawDevices.speaker),
    }),
    [rawDevices],
  );

  const filteredDevices = useMemo(
    () => ({
      microphone: applyVisibilityFilter(
        selectableDevices.microphone,
        filters.microphone,
      ),
      video: applyVisibilityFilter(selectableDevices.video, filters.video),
      speaker: applyVisibilityFilter(selectableDevices.speaker, filters.speaker),
    }),
    [selectableDevices, filters],
  );

  const devices = useMemo(() => {
    if (filterConfigs.length === 0) return filteredDevices;
    const next = { ...filteredDevices };
    filterConfigs.forEach(({ type, filterFn }) => {
      if (!filterFn) return;
      next[type] = filterFn(next[type]);
    });
    return next;
  }, [filteredDevices, filterConfigs]);

  return {
    devices,
    rawDevices,
  };
};
